/*----------------------------------
- PUBLIC CELL VALUES
----------------------------------*/

/**
 * @docs docs/features/website-domains-hubs
 * @adr  ADR-0028
 * @rule A public cell is the shared resolver's value (`resolveTableCellValue`, the one Product's table reads),
 *       minus what the website never shows: no checked time (no "Availability verified", "Last verified" or
 *       "Checked" line, no "Last verified N d ago" under a reading, and a reading that is only its own age
 *       reads "–") and no score beside the rating. Nothing is computed here; lines are dropped, never made.
 *
 * Why a website layer rather than a change in the resolver: Product's Browse keeps those dates in its hover
 * (a member re-checks a name from the drawer), so the rule is the public page's, not the value's.
 */

/*----------------------------------
- DEPENDANCES
----------------------------------*/

import {
    TIMING_AVAILABILITY_UNVERIFIED_NOTE,
    countFilledCells,
    resolveTableCellValue,
} from '@uniquedomains/components/domains/table/cellValues';
import {
    PUBLIC_RATING_BANDS,
    PUBLIC_RATING_CONFIDENCE,
} from '@/client/catalogs/landing/domains/publicTableColumns';
import { PUBLIC_PREVIEW_TABLE_COPY, fillPreviewCopy } from '@/client/catalogs/landing/domains/publicPreviewCopy';

// Types
import type { TableCellRow, TableCellValue } from '@uniquedomains/components/domains/table/cellValues';
import type { DomainsSearchPublicTableRow } from '@uniquedomains/components/contracts/domains/search';
import type { NameRowBand, NameRowRatingBand } from '@uniquedomains/components/contracts/domains/nameRow';

/*----------------------------------
- TYPES
----------------------------------*/

/** The rating as the kit `Rating` draws it, with the one-line hover that names its band and confidence. */
export type PublicRating = { band: NameRowRatingBand; confidence: NameRowBand; hover: string };

/*----------------------------------
- CONSTANTS
----------------------------------*/

/** The hover lines that carry a checked time (`cellValues.ts`, the timing case). */
const CHECKED_TIME_KEYS: readonly string[] = ['Availability verified', 'Last verified', 'Checked'];

/*----------------------------------
- HELPERS
----------------------------------*/

/**
 * The note under a timing card. A registrable name's "nobody has verified" note comes from the reading's verification
 * state, never from the absent date: the public row carries no `availabilityVerifiedAt` at all, so the resolver would
 * write the note on every registrable name, verified ones included (round 2, review M9). Every other note is the
 * resolver's.
 */
const publicTimingNote = (value: TableCellValue, projection: DomainsSearchPublicTableRow | null): string | null => {
    if (projection?.timing.state !== 'registrable') return value.hover?.note ?? null;
    return projection.timing.verification.state === 'unverified' ? TIMING_AVAILABILITY_UNVERIFIED_NOTE : null;
};

/**
 * The timing cell without its checked time. A `stale` reading's label is its own age ("Verified 10 d ago"), so
 * the cell reads "–" and its "may be out of date" note goes with it (the mock's expired list, nowadays.io).
 */
const publicTiming = (value: TableCellValue, projection: DomainsSearchPublicTableRow | null): TableCellValue => {
    if (projection?.timing.state === 'stale') return { kind: 'empty' };
    if (value.kind !== 'timing') return value;
    const lines = value.hover?.lines.filter((line) => !CHECKED_TIME_KEYS.includes(line.key)) ?? [];
    const note = publicTimingNote(value, projection);
    return {
        ...value,
        secondary: null,
        hover: lines.length > 0 || note ? { title: value.hover?.title ?? '', lines, note } : undefined,
    };
};

/*----------------------------------
- PUBLIC API
----------------------------------*/

/**
 * One cell of the public table, phone card or Markdown mirror. The rating column reads the band and its confidence
 * in words ("Strong choice · High confidence"), since a public row carries no score; the table and the cards draw that
 * band from `resolvePublicRating` as the kit `Rating`.
 */
export const resolvePublicCellValue = (
    columnId: string,
    row: TableCellRow,
    projection: DomainsSearchPublicTableRow | null,
): TableCellValue => {
    if (columnId === 'verdict') {
        const rating = resolvePublicRating(projection);
        return rating ? { kind: 'text', value: rating.hover, tone: 'ink' } : { kind: 'empty' };
    }
    const value = resolveTableCellValue(columnId, row, projection);
    return columnId === 'timing' ? publicTiming(value, projection) : value;
};

/** The served rating as a band and its confidence dots, or null when the row carries none. */
export const resolvePublicRating = (projection: DomainsSearchPublicTableRow | null): PublicRating | null => {
    const verdict = projection?.verdict;
    if (!verdict) return null;
    return {
        band: PUBLIC_RATING_BANDS[verdict.label],
        confidence: PUBLIC_RATING_CONFIDENCE[verdict.confidence],
        hover: fillPreviewCopy(PUBLIC_PREVIEW_TABLE_COPY.ratingHover, { label: verdict.label, confidence: verdict.confidence }),
    };
};

/**
 * How many rows of this preview carry a reading, per column, as the page draws them: through the public layer, so a
 * stale timing the page shows as "–" is not counted and the rating counts from its band, never from a score (review
 * m-1, m-39). Presentation only: it describes the rows on screen, never the catalog.
 */
export const countPublicFilledCells = (
    rows: ReadonlyArray<{ row: TableCellRow; projection: DomainsSearchPublicTableRow | null }>,
    columnIds: readonly string[],
): Record<string, number> => countFilledCells(rows, columnIds, resolvePublicCellValue);
